import isNil from 'lodash/isNil';
import {
  IMeasureDisplayFormatFieldsFragment,
  Maybe,
  Order,
  RelationalOperatorKey,
} from '../../../utils/apollo/graphqlSchema';
import {
  IDisplayedMeasure,
  IDisplayedSlice,
  IParsedChartDataExpression,
  IParsedExpressionColumn,
  TableSortType,
} from '../interfaces';
import { mapComparisonName } from '../../../utils/redux/mergeExpressionComparisonData';
import { isNotOverallSlice, isOverallSlice } from '../utils';
import {
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
  IAnalysisColorConfig,
} from '../utils/analysisColorConfig';
import { ColumnFilterConfig } from '../../MultiGridTable/ColumnFilterWidget/types';
import { assertNotNil } from '../../../utils/types';
import { theme } from '../../../styles/theme';

const N_A_VALUE = 'N/A';

export interface ITableCell {
  value: string | number;
  backgroundColor?: string;
  isDisabled?: boolean;
}

export const getFirstNonOverallSlice = (displayedSlices: IDisplayedSlice[]) => {
  return displayedSlices.find((slice) => isNotOverallSlice(slice));
};

export const generateBaseHeaders = (
  chartData: IParsedChartDataExpression,
  displayedSlices: IDisplayedSlice[],
) => {
  const measureHeader = 'Measure';
  let chartHeaders = [measureHeader];

  // overall slices do not contain hints we care about for the table header
  const firstNonOverallSlice = getFirstNonOverallSlice(displayedSlices);

  // use hints from the first non overall slice, this will contain
  // all the individual breakdowns & segments
  if (!isNil(firstNonOverallSlice)) {
    const sliceHeaders = firstNonOverallSlice.hint.map((h) => h.name);
    if (!isNil(firstNonOverallSlice?.rank)) {
      chartHeaders = ['Rank', ...sliceHeaders, measureHeader];
    } else {
      chartHeaders = [...sliceHeaders, measureHeader];
    }
  }

  chartData.data.forEach((item) => {
    chartHeaders.push(item.date as string);
  });

  return chartHeaders;
};

// groups columns together by measures
// all groups & segments of measure A will show first, then measure B, ...
const groupColumnsByMeasure = (
  displayedMeasures: IDisplayedMeasure[],
  columns: IParsedExpressionColumn[],
): IParsedExpressionColumn[] => {
  const formattedColumns: IParsedExpressionColumn[] = [];
  displayedMeasures.forEach((measure) => {
    columns.forEach((column) => {
      if (column.measureKey === measure.key) {
        formattedColumns.push(column);
      }
    });
  });
  return formattedColumns;
};

interface IBaseCell extends ITableCell {
  color: string;
  isFixedCell: boolean;
  format?: Maybe<IMeasureDisplayFormatFieldsFragment>;
  isRankCell?: boolean;
  isGroupCell?: boolean;
  sliceColor?: string;
  isSliceVisible?: boolean;
  sliceKey?: string;
  isMeasureCell?: boolean;
  measureLabel?: string;
  measureColor?: string;
}

type IBaseRow = {
  cells: IBaseCell[];
};

interface IGenerateBaseRowsArgs {
  chartData: IParsedChartDataExpression;
  displayedMeasures: IDisplayedMeasure[];
  displayedSlices: IDisplayedSlice[];
  colorConfig: IAnalysisColorConfig;
  showCompare: boolean;
  showMeasure: boolean;
}

export const generateBaseRows = ({
  chartData,
  displayedMeasures,
  displayedSlices,
  colorConfig,
  showCompare,
  showMeasure,
}: IGenerateBaseRowsArgs): IBaseRow[] => {
  const { columns, slices } = chartData;

  const firstNonOverallSlice = getFirstNonOverallSlice(displayedSlices);
  const groupedColumns = groupColumnsByMeasure(displayedMeasures, columns);

  return groupedColumns.map((column) => {
    const {
      measureKey,
      values,
      measureName,
      isComparison,
      isFutureComparison,
    } = column;
    const measure = displayedMeasures.find(
      (displayedMeasure) => displayedMeasure.key === measureKey,
    );
    assertNotNil(measure);

    const sliceKey = column.originalSliceKey || column.sliceKey;
    // displayedSlice will return information about visibility and color
    const displayedSlice = displayedSlices.find((ds) => ds.key === sliceKey);
    // slice will give us original information
    const slice = slices.find(
      (aliasableSlice) => aliasableSlice.key === column.sliceKey,
    );

    const isMeasureVisible = measure.isVisible;
    const isSliceVisible = displayedSlice?.isVisible ?? false;

    // KBT-1014 disable compare slices in Compare mode
    // when showCompare setting is off
    const isDisabled =
      !isMeasureVisible ||
      (slice?.isComparison && !showCompare) ||
      (!slice?.isComparison && !showMeasure);

    const measureTextMapped = isComparison
      ? mapComparisonName(measureName, isFutureComparison ?? false)
      : measureName;

    const measureColor = getMeasureColorFromColorConfig(
      colorConfig,
      measureKey,
    );
    const sliceColor = !isNil(slice)
      ? getSliceColorFromColorConfig(colorConfig, sliceKey)
      : undefined;

    const cells: IBaseCell[] = [
      // Measure Cell
      {
        value: measureTextMapped,
        color: theme.colors.secondary800,
        backgroundColor: theme.colors.tableHeader,
        isDisabled,
        isFixedCell: true,
        isSliceVisible,
        sliceKey,
        isMeasureCell: true,
        measureLabel: measure?.label,
        measureColor: isDisabled ? theme.colors.secondary400 : measureColor,
      },
      // Values
      ...values.map((value) => ({
        value: value ?? N_A_VALUE,
        color: theme.colors.secondary800,
        backgroundColor: theme.colors.level2,
        isDisabled,
        isFixedCell: false,
        format: measure.format,
        isSliceVisible,
        sliceKey,
        sliceColor,
      })),
    ];

    if (displayedSlice && slice && displayedSlices.length > 0) {
      const sliceCells: IBaseCell[] = [];

      if (isOverallSlice(displayedSlice)) {
        // special case for overall, overall will only have 1 column as specified by the hint
        // however, the other slices will contain more hint entries than the overall slice.
        // this will align all the overall slices with the others, by leaving certain columns blank
        firstNonOverallSlice?.hint?.forEach((_, index) => {
          sliceCells.push({
            value: index === 0 ? slice.hint[0].value : N_A_VALUE,
            color: theme.colors.secondary900,
            backgroundColor: theme.colors.tableHeader,
            isDisabled,
            isFixedCell: true,
            isGroupCell: true,
            sliceColor: index === 0 ? sliceColor : undefined,
            isSliceVisible,
            sliceKey,
          });
        });
      } else {
        slice.hint.forEach((hint, index) => {
          sliceCells.push({
            value: hint.value, // sliceName,
            color: theme.colors.secondary900,
            backgroundColor: theme.colors.tableHeader,
            isDisabled,
            isFixedCell: true,
            isGroupCell: true,
            sliceColor: index === 0 ? sliceColor : undefined,
            isSliceVisible,
            sliceKey,
          });
        });
      }

      // if rank exists in the table, create a new column just for the rank
      if (!isNil(firstNonOverallSlice?.rank)) {
        sliceCells.unshift({
          value: slice.rank ?? '',
          color: theme.colors.secondary800,
          backgroundColor: theme.colors.tableHeader,
          isDisabled,
          isFixedCell: true,
          isRankCell: true,
          sliceColor,
          isSliceVisible,
          sliceKey,
        });
      }

      cells.unshift(...sliceCells);
    }

    return {
      cells,
    };
  });
};

// group sort follows a different algorithm for the rest,
// and only applies to group cells.
// For the Group column only, when user mouse over the sorting icon, show a popup to choose between two sorting modes:
// Alphabetical: sort as string/text, where you get “1, 15, 21, 5, 9, …” (default)
// Numerical: sort as numbers, where you get “1, 5, 9, 15, 21 …”
export const groupCellSort = ({
  sortType,
  sortDirection,
  aValue,
  bValue,
}: {
  sortType?: TableSortType;
  sortDirection?: Order;
  aValue: string | number;
  bValue: string | number;
}) => {
  if (sortType === TableSortType.NUMERICAL) {
    const numericalA = parseFloat(aValue.toString());
    const numericalB = parseFloat(bValue.toString());

    // if numerical sort and are parsable numbers
    // sort by the numeric value
    if (!isNaN(numericalA) && !isNaN(numericalB)) {
      return sortDirection === Order.Asc
        ? numericalA - numericalB
        : numericalB - numericalA;
    } else if (aValue === N_A_VALUE || bValue === N_A_VALUE) {
      // if one of the values is N/A, N/A should be sorted last
      return aValue === N_A_VALUE ? 1 : -1;
    }
  }

  // if value is of string type, compare non-numeric strings
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return sortDirection === Order.Asc
      ? aValue.localeCompare(bValue, 'en')
      : bValue.localeCompare(aValue, 'en');
  }

  return 0;
};

export const defaultSort = ({
  sortDirection,
  aValue,
  bValue,
}: {
  sortDirection?: Order;
  aValue: string | number;
  bValue: string | number;
}) => {
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sortDirection === Order.Asc ? aValue - bValue : bValue - aValue;
  } else if (typeof aValue === 'string' && typeof bValue === 'string') {
    return sortDirection === Order.Asc
      ? aValue.localeCompare(bValue, 'en', { numeric: true })
      : bValue.localeCompare(aValue, 'en', { numeric: true });
  } else if (aValue === N_A_VALUE || bValue === N_A_VALUE) {
    // if one of the values is N/A, N/A should be sorted last
    return aValue === N_A_VALUE ? 1 : -1;
  }

  return 0;
};

export const generateDefaultTableColumnOrderMapping = (
  rows: IBaseRow[],
): number[] => {
  const firstRow = rows[0];

  if (!firstRow) {
    return [];
  }

  return firstRow.cells.map((_, index) => index);
};

export const generateFilteredRows = ({
  filterConfig,
  defaultRows,
  colIndex,
}: {
  filterConfig: ColumnFilterConfig;
  defaultRows: IBaseRow[];
  colIndex: number;
}) => {
  const { condition, values } = filterConfig;
  const normalizeValues = values?.map((value) =>
    value.toString().toLowerCase(),
  );

  return defaultRows.filter((row) => {
    const cell = row.cells[colIndex];
    const cellValue = cell.value?.toString().toLowerCase();

    if (!isNil(cellValue)) {
      switch (condition) {
        case RelationalOperatorKey.Equal:
          return normalizeValues?.includes(cellValue);
        case RelationalOperatorKey.Notequal:
          return !normalizeValues?.includes(cellValue);
        case RelationalOperatorKey.StartsWith:
          return normalizeValues?.some((value) =>
            cellValue.startsWith(value.toString().toLowerCase()),
          );
        case RelationalOperatorKey.NotStartsWith:
          return !normalizeValues?.some((value) =>
            cellValue.startsWith(value.toString().toLowerCase()),
          );
        case RelationalOperatorKey.EndsWith:
          return normalizeValues?.some((value) =>
            cellValue.endsWith(value.toString().toLowerCase()),
          );
        case RelationalOperatorKey.NotEndsWith:
          return !normalizeValues?.some((value) =>
            cellValue.endsWith(value.toString().toLowerCase()),
          );
        case RelationalOperatorKey.Contains:
          return normalizeValues?.some((value) =>
            cellValue.includes(value.toString().toLowerCase()),
          );
        case RelationalOperatorKey.NotContains:
          return !normalizeValues?.some((value) =>
            cellValue.includes(value.toString().toLowerCase()),
          );
        default:
          return true;
      }
    }

    return true;
  });
};
